import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DisconnectionDetails, useConversation } from '@elevenlabs/react'
import { Pages } from '../../Pages'
import { NoteType } from '../../enums/NoteType'
import authApi from '@/api/auth/AuthApi'
import kitApi from '@/api/kit/KitApi'
import personApi, {
    type PersonNetworkImportStatusSummaryDto,
} from '@/api/person/PersonApi'
import type { PersonDto } from '@/api/person/models/PersonDto'
import type { CsPersonSearchResultDto } from '@/api/person/models/searchCs/SearchPersonsCsDtos'
import { normalizeLinkedInProfileUrl } from '@/utils/linkedin'
import KitTestIntroCard from './components/KitTestIntroCard'
import ProfileSourceCard from './components/ProfileSourceCard'
import StartConversationCard from './components/StartConversationCard'
import KitDynamicVariablesCard from './components/KitDynamicVariablesCard'
import KitDebugConsole from './components/KitDebugConsole'
import KitToolTesterCard from './components/KitToolTesterCard'
import useKitConvoDebug from './hooks/useKitConvoDebug'
import type { LookupNote, PersonSource, SessionInfo } from './types'
import { kitTestThemeVars } from './theme'
import LoadingSpinner from '../common/loading/LoadingSpinner'

const KIT_AGENT_PRESETS = [
    {
        label: 'Tools-R-Us v2 (Network Kit)',
        id: 'agent_6001kh40ydtrek8vqpsz9qhhf93h',
    },
    {
        label: 'Kit-test - Test Harness for Contxt Integration',
        id: 'agent_0301kf3h75zbfdpv4gqbr20xmepw',
    },
] as const
const DEFAULT_AGENT_PRESET_ID = KIT_AGENT_PRESETS[0].id
const DEFAULT_KIT_TOOL_API_KEY =
    'M9cx32DnGYV39OoGgCfFNlxZdhDAb4y9U6e0scq6A6k0P2Ut'
const INVALID_LINKEDIN_URL_MESSAGE =
    'Please enter a full LinkedIn profile URL like https://www.linkedin.com/in/your-name.'
const MATCH_LIMIT = 5
const NO_PERSON_PROFILE_DATA = 'NO PERSON PROFILE DATA'

const containerClass = 'mx-auto w-full max-w-none px-4 md:px-6 lg:px-8'

type CallMode = 'real' | 'mock'
type CallStatus = 'disconnected' | 'connecting' | 'connected' | 'disconnecting'
type PendingRealConvoStart = {
    personId?: string
    dynamicVariables: Record<string, string>
    source: 'network_kit'
    hasLoggedConvo: boolean
}

const createMockConversationId = (): string =>
    `mock_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`

export default function KitTest(): React.JSX.Element | null {
    const navigate = useNavigate()
    const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null)
    const [loadingSession, setLoadingSession] = useState(true)
    const [sourceChoice, setSourceChoice] = useState<PersonSource>('user')
    const [hasChosenSource, setHasChosenSource] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [lookupNote, setLookupNote] = useState<LookupNote | null>(null)
    const [linkedinUrl, setLinkedinUrl] = useState('')
    const [csMatches, setCsMatches] = useState<CsPersonSearchResultDto[]>([])
    const [selectedCsPersonId, setSelectedCsPersonId] = useState<string | null>(
        null,
    )
    const [csNetworkImportStatuses, setCsNetworkImportStatuses] = useState<
        Record<string, PersonNetworkImportStatusSummaryDto>
    >({})
    const [isLoadingNetworkStatuses, setIsLoadingNetworkStatuses] =
        useState(false)
    const [csNetworkConnectionCounts, setCsNetworkConnectionCounts] = useState<
        Record<string, number>
    >({})
    const [
        isLoadingNetworkConnectionCounts,
        setIsLoadingNetworkConnectionCounts,
    ] = useState(false)
    const [linkedInPerson, setLinkedInPerson] = useState<PersonDto | null>(null)
    const [linkedInPersonCsId, setLinkedInPersonCsId] = useState<string | null>(
        null,
    )
    const [isSearching, setIsSearching] = useState(false)
    const [isFindingRandom, setIsFindingRandom] = useState(false)
    const [isPreparingPerson, setIsPreparingPerson] = useState(false)
    const [isCheckingImportStatus, setIsCheckingImportStatus] = useState(false)
    const [importStatus, setImportStatus] =
        useState<PersonNetworkImportStatusSummaryDto | null>(null)
    const [importStatusPersonId, setImportStatusPersonId] = useState<
        string | null
    >(null)
    const [isStarting, setIsStarting] = useState(false)
    const [startMode, setStartMode] = useState<CallMode | null>(null)
    const [isStopping, setIsStopping] = useState(false)
    const [callMode, setCallMode] = useState<CallMode | null>(null)
    const [mockStatus, setMockStatus] = useState<CallStatus>('disconnected')
    const [conversationMode, setConversationMode] = useState<
        'speaking' | 'listening' | null
    >(null)
    const conversationIdRef = useRef<string | null>(null)
    const loggedConversationIdRef = useRef(false)
    const callModeRef = useRef<CallMode | null>(null)
    const pendingRealConvoStartRef = useRef<PendingRealConvoStart | null>(null)
    const [activeConvoId, setActiveConvoId] = useState<string | null>(null)
    const [kitToolApiKey, setKitToolApiKey] = useState(DEFAULT_KIT_TOOL_API_KEY)
    const [agentPresetId, setAgentPresetId] = useState<string>(
        DEFAULT_AGENT_PRESET_ID,
    )
    const [agentId, setAgentId] = useState<string>(DEFAULT_AGENT_PRESET_ID)
    const [lastDynamicVariables, setLastDynamicVariables] = useState<Record<
        string,
        string
    > | null>(null)

    const startBackendConvoIfNeeded = useCallback(
        async (conversationId: string) => {
            const normalizedConversationId = conversationId?.trim()
            if (!normalizedConversationId) {
                return
            }

            const pendingStart = pendingRealConvoStartRef.current
            if (!pendingStart || pendingStart.hasLoggedConvo) {
                return
            }

            pendingStart.hasLoggedConvo = true
            setLastDynamicVariables(pendingStart.dynamicVariables)

            try {
                await kitApi.startConvo({
                    convId: normalizedConversationId,
                    personId: pendingStart.personId,
                    dynamicVariables: pendingStart.dynamicVariables,
                    source: pendingStart.source,
                })
                setActiveConvoId(normalizedConversationId)
            } catch (kitError) {
                pendingStart.hasLoggedConvo = false
                console.error(
                    'Failed to log Kit conversation in backend',
                    kitError,
                )
                setError(
                    kitError instanceof Error
                        ? kitError.message
                        : 'Call started but could not be logged.',
                )
            }
        },
        [],
    )

    const markConvoCallEndedInBackend = useCallback(
        async (endedConversationId: string) => {
            await kitApi
                .markConvoCallEnded({
                    convId: endedConversationId,
                })
                .catch((kitError) => {
                    console.error(
                        'Failed to mark Kit conversation as call ended in Kit Test',
                        {
                            convId: endedConversationId,
                            kitError,
                        },
                    )
                })
        },
        [],
    )

    const { startSession, endSession, status, getOutputVolume } =
        useConversation({
            onConnect: ({ conversationId }) => {
                const normalizedConversationId = conversationId?.trim() || null
                conversationIdRef.current = normalizedConversationId
                if (
                    normalizedConversationId &&
                    !loggedConversationIdRef.current
                ) {
                    console.log(
                        'ElevenLabs conversation id',
                        normalizedConversationId,
                    )
                    loggedConversationIdRef.current = true
                }
                if (
                    callModeRef.current === 'real' &&
                    normalizedConversationId
                ) {
                    void startBackendConvoIfNeeded(normalizedConversationId)
                }
            },
            onDisconnect: (details: DisconnectionDetails) => {
                const endedConversationId =
                    conversationIdRef.current?.trim() || null

                if (callModeRef.current === 'real' && endedConversationId) {
                    void markConvoCallEndedInBackend(endedConversationId)
                }

                pendingRealConvoStartRef.current = null
                setConversationMode(null)
                if (details?.reason !== 'error') {
                    return
                }
                const message =
                    details?.context instanceof CloseEvent
                        ? details.context.reason || 'Unable to connect to Kit.'
                        : 'Unable to connect to Kit.'
                setError(message)
            },
            onModeChange: ({ mode }) => {
                setConversationMode(mode)
            },
            onError: (message) => {
                setError(message || 'Something went wrong starting the call.')
            },
        })

    useEffect(() => {
        callModeRef.current = callMode
    }, [callMode])

    useEffect(() => {
        if (callMode !== 'real') {
            return
        }
        if (status === 'disconnected' && !isStarting && !isStopping) {
            setCallMode(null)
        }
    }, [callMode, isStarting, isStopping, status])

    useEffect(() => {
        if (status !== 'disconnected') {
            return
        }
        pendingRealConvoStartRef.current = null
    }, [status])

    useEffect(() => {
        let active = true

        const loadSessionUser = async () => {
            try {
                const res = await authApi.getMeDetails()
                if (!active) return

                if (!res.user) {
                    navigate(Pages.Login, { replace: true })
                    return
                }

                setSessionInfo({
                    user: {
                        id: res.user.id,
                        email: res.user.email,
                        name: res.user.name?.trim() || res.user.email,
                    },
                    person: res.person ?? null,
                })
            } catch (err) {
                console.error('Failed to load session details', err)
                if (active) {
                    navigate(Pages.Login, { replace: true })
                }
            } finally {
                if (active) {
                    setLoadingSession(false)
                }
            }
        }

        loadSessionUser()

        return () => {
            active = false
        }
    }, [navigate])

    useEffect(() => {
        if (!sessionInfo || hasChosenSource) {
            return
        }
        setSourceChoice(sessionInfo.person ? 'user' : 'linkedin')
    }, [hasChosenSource, sessionInfo])

    useEffect(() => {
        return () => {
            if (callModeRef.current === 'real') {
                endSession().catch((endError) => {
                    console.error(
                        'Failed to end ElevenLabs session on unmount',
                        {
                            endError,
                        },
                    )
                })
                return
            }

            if (callModeRef.current === 'mock') {
                const endedConversationId =
                    conversationIdRef.current?.trim() || null
                if (!endedConversationId) {
                    return
                }

                void markConvoCallEndedInBackend(endedConversationId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectedCsPerson = useMemo(
        () =>
            csMatches.find((result) => result.id === selectedCsPersonId) ??
            null,
        [csMatches, selectedCsPersonId],
    )

    useEffect(() => {
        if (csMatches.length === 0) {
            setCsNetworkImportStatuses({})
            setIsLoadingNetworkStatuses(false)
            return
        }

        const personIds = Array.from(
            new Set(
                csMatches
                    .map((result) =>
                        (result.existingPersonId ?? result.id)?.trim(),
                    )
                    .filter(Boolean) as string[],
            ),
        )

        if (personIds.length === 0) {
            setCsNetworkImportStatuses({})
            setIsLoadingNetworkStatuses(false)
            return
        }

        let cancelled = false
        setIsLoadingNetworkStatuses(true)
        ;(async () => {
            try {
                const statuses =
                    await personApi.getNetworkImportStatuses(personIds)
                if (!cancelled) {
                    setCsNetworkImportStatuses(statuses)
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load network import statuses', err)
                    setCsNetworkImportStatuses({})
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingNetworkStatuses(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [csMatches])

    useEffect(() => {
        if (csMatches.length === 0) {
            setCsNetworkConnectionCounts({})
            setIsLoadingNetworkConnectionCounts(false)
            return
        }

        const personIds = Array.from(
            new Set(
                csMatches
                    .map((result) => result.id?.trim())
                    .filter(Boolean) as string[],
            ),
        )

        if (personIds.length === 0) {
            setCsNetworkConnectionCounts({})
            setIsLoadingNetworkConnectionCounts(false)
            return
        }

        let cancelled = false
        setIsLoadingNetworkConnectionCounts(true)
        ;(async () => {
            try {
                const counts =
                    await personApi.getCsNetworkConnectionCounts(personIds)
                if (!cancelled) {
                    setCsNetworkConnectionCounts(counts)
                }
            } catch (err) {
                if (!cancelled) {
                    console.error(
                        'Failed to load network connection counts',
                        err,
                    )
                    setCsNetworkConnectionCounts({})
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingNetworkConnectionCounts(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [csMatches])

    const handleSourceChange = useCallback((nextSource: PersonSource) => {
        setSourceChoice(nextSource)
        setHasChosenSource(true)
        setError(null)
    }, [])

    const handleSelectMatch = useCallback((person: CsPersonSearchResultDto) => {
        setSelectedCsPersonId(person.id)
        setLinkedInPerson(null)
        setLinkedInPersonCsId(null)
        setError(null)
    }, [])

    const handleAgentPresetChange = useCallback((nextAgentId: string) => {
        const matchedPreset = KIT_AGENT_PRESETS.find(
            (preset) => preset.id === nextAgentId,
        )
        if (!matchedPreset) {
            return
        }

        setAgentPresetId(matchedPreset.id)
        setAgentId(matchedPreset.id)
        setError(null)
    }, [])

    const handleAgentIdChange = useCallback((nextAgentId: string) => {
        setAgentId(nextAgentId)
        const matchedPreset = KIT_AGENT_PRESETS.find(
            (preset) => preset.id === nextAgentId.trim(),
        )
        if (matchedPreset) {
            setAgentPresetId(matchedPreset.id)
        }
        setError(null)
    }, [])

    const runLinkedInLookup = useCallback(
        async (rawUrl: string) => {
            if (isSearching) return

            const normalized = normalizeLinkedInProfileUrl(rawUrl)
            if (!normalized) {
                setLookupNote({
                    type: NoteType.ERROR,
                    message: INVALID_LINKEDIN_URL_MESSAGE,
                })
                setCsMatches([])
                setSelectedCsPersonId(null)
                setLinkedInPerson(null)
                setLinkedInPersonCsId(null)
                return
            }

            setIsSearching(true)
            setError(null)
            setLookupNote(null)
            setLinkedinUrl(normalized)
            setCsMatches([])
            setSelectedCsPersonId(null)
            setLinkedInPerson(null)
            setLinkedInPersonCsId(null)

            try {
                const response = await personApi.searchPersonsCsByLinkedInUrl(
                    normalized,
                    1,
                    MATCH_LIMIT,
                )
                const results = response.results ?? []
                setCsMatches(results)
                setSelectedCsPersonId(results[0]?.id ?? null)

                if (!results.length) {
                    setLookupNote({
                        type: NoteType.WARNING,
                        message:
                            'No matching profile was found in cold storage. This Kit Test needs a cold storage profile, so you cannot continue.',
                    })
                    return
                }

                setLookupNote({
                    type: NoteType.INFO,
                    message:
                        results.length > 1
                            ? 'Multiple matches found. Select the correct profile before starting.'
                            : 'Profile found in cold storage. You can start the Kit Test when ready.',
                })
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to search cold storage. Please try again.'
                setLookupNote({
                    type: NoteType.ERROR,
                    message,
                })
            } finally {
                setIsSearching(false)
            }
        },
        [isSearching],
    )

    const handleLinkedInLookup = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            await runLinkedInLookup(linkedinUrl)
        },
        [linkedinUrl, runLinkedInLookup],
    )

    const handleFindRandom = useCallback(async () => {
        if (isSearching || isFindingRandom) return

        setIsFindingRandom(true)
        setError(null)
        setLookupNote({
            type: NoteType.INFO,
            message: 'Finding a random cold storage profile...',
        })

        try {
            const result = await personApi.findRandomKitTestPerson({
                minWorkHistoryCount: 1,
                minNetworkConnectionCount: 10,
                timeoutMs: 120_000,
            })

            setLinkedinUrl(result.linkedInUrl)
            await runLinkedInLookup(result.linkedInUrl)
        } catch (err: unknown) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Unable to find a random profile right now.'
            setLookupNote({
                type: NoteType.ERROR,
                message,
            })
        } finally {
            setIsFindingRandom(false)
        }
    }, [isFindingRandom, isSearching, runLinkedInLookup])

    const prepareLinkedInPerson =
        useCallback(async (): Promise<PersonDto | null> => {
            if (!selectedCsPerson) {
                return null
            }

            if (linkedInPerson && linkedInPersonCsId === selectedCsPerson.id) {
                return linkedInPerson
            }

            setIsPreparingPerson(true)
            setError(null)

            try {
                const existingPersonId =
                    selectedCsPerson.existingPersonId?.trim()
                const resolvedPersonId = existingPersonId
                    ? existingPersonId
                    : (
                          await personApi.importPersonFromCs({
                              personId: selectedCsPerson.id,
                          })
                      ).personId
                const personRes =
                    await personApi.getPersonById(resolvedPersonId)
                setLinkedInPerson(personRes.person)
                setLinkedInPersonCsId(selectedCsPerson.id)
                return personRes.person
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to import the person. Please try again.'
                setError(message)
                return null
            } finally {
                setIsPreparingPerson(false)
            }
        }, [linkedInPerson, linkedInPersonCsId, selectedCsPerson])

    const resolvePersonForConversation =
        useCallback(async (): Promise<PersonDto | null> => {
            if (sourceChoice === 'user') {
                return sessionInfo?.person ?? null
            }

            if (!selectedCsPerson) {
                return null
            }

            return await prepareLinkedInPerson()
        }, [prepareLinkedInPerson, selectedCsPerson, sessionInfo, sourceChoice])

    const ensurePersonNetworkImportStatus = useCallback(
        async (
            personId: string,
        ): Promise<PersonNetworkImportStatusSummaryDto | null> => {
            const trimmedPersonId = personId.trim()
            if (!trimmedPersonId) {
                return null
            }

            setIsCheckingImportStatus(true)
            try {
                const initialStatuses =
                    await personApi.getNetworkImportStatuses([trimmedPersonId])
                const initialStatus = initialStatuses[trimmedPersonId]
                if (!initialStatus) {
                    return null
                }

                setImportStatusPersonId(trimmedPersonId)
                setImportStatus(initialStatus)

                const shouldQueueImport =
                    initialStatus.status !== 'COMPLETE' &&
                    initialStatus.status !== 'IN_PROGRESS'

                if (shouldQueueImport) {
                    await personApi.importPersonNetwork({
                        personId: trimmedPersonId,
                    })
                }

                if (!shouldQueueImport) {
                    return initialStatus
                }

                const refreshedStatuses =
                    await personApi.getNetworkImportStatuses([trimmedPersonId])
                const refreshedStatus =
                    refreshedStatuses[trimmedPersonId] ?? initialStatus
                setImportStatus(refreshedStatus)
                return refreshedStatus
            } finally {
                setIsCheckingImportStatus(false)
            }
        },
        [],
    )

    const effectiveStatus = callMode === 'mock' ? mockStatus : status
    const effectiveConversationMode =
        callMode === 'mock' ? null : conversationMode
    const getOutputVolumeSafe = useCallback(
        () => (callMode === 'mock' ? 0 : getOutputVolume()),
        [callMode, getOutputVolume],
    )

    const isActiveCall =
        effectiveStatus === 'connecting' ||
        effectiveStatus === 'connected' ||
        effectiveStatus === 'disconnecting' ||
        isStarting

    const {
        toolCalls,
        transcriptRawData,
        transcriptTurns,
        personMentions,
        companyMentions,
        locationMentions,
        targetJobTitleMentions,
        targetSalaryMentions,
        currentJobSearchParams,
        jobsOffered,
        jobSearchExecutions,
        scoring,
        toolCallsError,
        flashingToolCallIds,
        resetDebugState,
    } = useKitConvoDebug({
        conversationId: activeConvoId,
        isActiveCall,
    })

    const startKitSession = useCallback(
        async (mode: CallMode) => {
            if (!sessionInfo?.user) return
            const resolvedAgentId = agentId.trim()
            if (mode === 'real' && !resolvedAgentId) {
                setError('Agent id is required to start a real call.')
                return
            }

            setIsStarting(true)
            setStartMode(mode)
            setCallMode(mode)
            setError(null)
            setConversationMode(null)
            conversationIdRef.current = null
            loggedConversationIdRef.current = false
            pendingRealConvoStartRef.current = null
            setActiveConvoId(null)
            setLastDynamicVariables(null)
            resetDebugState()

            if (mode === 'mock') {
                setMockStatus('disconnected')
            }

            if (mode === 'real') {
                try {
                    await navigator.mediaDevices.getUserMedia({ audio: true })
                } catch (permissionError) {
                    console.error(
                        'Microphone permission was denied for ElevenLabs conversation',
                        permissionError,
                    )
                    setError(
                        'We need permission to use your microphone before starting the call.',
                    )
                    setIsStarting(false)
                    setStartMode(null)
                    setCallMode(null)
                    return
                }
            }

            const person = await resolvePersonForConversation()
            setImportStatus(null)
            setImportStatusPersonId(null)

            try {
                if (person) {
                    const importStatus = await ensurePersonNetworkImportStatus(
                        person.id,
                    )
                    if (!importStatus) {
                        setError(
                            'Unable to determine network import status for this person.',
                        )
                        setCallMode(null)
                        return
                    }

                    if (!importStatus.statusId) {
                        setError(
                            'Unable to determine network import status id for this person.',
                        )
                        setCallMode(null)
                        return
                    }
                }

                const dynamicVariables: Record<string, string> = {
                    user_id: sessionInfo.user.id,
                    person_profile: NO_PERSON_PROFILE_DATA,
                    name: 'there',
                }

                if (person) {
                    dynamicVariables.first_name = person.firstName
                    dynamicVariables.name = person.firstName ?? ''
                    dynamicVariables.person_id = person.id
                    dynamicVariables.person_profile = JSON.stringify(person)
                }

                if (
                    sourceChoice === 'linkedin' &&
                    selectedCsPerson?.linkedInUrl
                ) {
                    dynamicVariables.linkedin_url = selectedCsPerson.linkedInUrl
                }

                if (mode === 'real') {
                    pendingRealConvoStartRef.current = {
                        personId: person?.id,
                        dynamicVariables,
                        source: 'network_kit',
                        hasLoggedConvo: false,
                    }
                }

                const conversationId =
                    mode === 'real'
                        ? await startSession({
                              agentId: resolvedAgentId,
                              connectionType: 'webrtc',
                              userId: sessionInfo.user.id,
                              dynamicVariables,
                          })
                        : createMockConversationId()

                if (conversationId) {
                    conversationIdRef.current = conversationId
                    if (!loggedConversationIdRef.current) {
                        console.log(
                            mode === 'mock'
                                ? 'Mock Kit conversation id'
                                : 'ElevenLabs conversation id',
                            conversationId,
                        )
                        loggedConversationIdRef.current = true
                    }

                    if (mode === 'mock') {
                        setMockStatus('connected')
                        setLastDynamicVariables(dynamicVariables)

                        try {
                            await kitApi.startConvo({
                                convId: conversationId,
                                personId: person?.id,
                                dynamicVariables,
                                source: 'network_kit',
                            })
                            setActiveConvoId(conversationId)
                        } catch (kitError) {
                            console.error(
                                'Failed to log Kit conversation in backend',
                                kitError,
                            )
                            setError(
                                kitError instanceof Error
                                    ? kitError.message
                                    : 'Call started but could not be logged.',
                            )
                        }
                    } else {
                        await startBackendConvoIfNeeded(conversationId)
                    }
                }
            } catch (startError) {
                if (mode === 'real' && conversationIdRef.current) {
                    void startBackendConvoIfNeeded(conversationIdRef.current)
                }
                console.error('Failed to start Kit conversation', startError)
                const message =
                    startError instanceof Error
                        ? startError.message
                        : 'Unable to start the call right now.'
                setError(message)
            } finally {
                if (mode === 'real' && !conversationIdRef.current) {
                    pendingRealConvoStartRef.current = null
                }
                setIsStarting(false)
                setStartMode(null)
            }
        },
        [
            ensurePersonNetworkImportStatus,
            resolvePersonForConversation,
            resetDebugState,
            selectedCsPerson,
            sessionInfo,
            sourceChoice,
            agentId,
            startBackendConvoIfNeeded,
            startSession,
        ],
    )

    const handleStart = useCallback(() => {
        void startKitSession('real')
    }, [startKitSession])

    const handleStartMock = useCallback(() => {
        void startKitSession('mock')
    }, [startKitSession])

    useEffect(() => {
        if (!importStatusPersonId) {
            return
        }
        if (importStatus?.status !== 'IN_PROGRESS') {
            return
        }

        let cancelled = false
        let timeoutId: number | undefined

        const poll = async () => {
            try {
                const latestStatuses = await personApi.getNetworkImportStatuses(
                    [importStatusPersonId],
                )
                if (cancelled) return
                const latest = latestStatuses[importStatusPersonId]
                if (latest) {
                    setImportStatus(latest)
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to poll network import status', err)
                }
            } finally {
                if (!cancelled) {
                    timeoutId = window.setTimeout(poll, 4000)
                }
            }
        }

        poll()

        return () => {
            cancelled = true
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId)
            }
        }
    }, [importStatus?.status, importStatusPersonId])

    useEffect(() => {
        if (!importStatusPersonId || !importStatus) {
            return
        }

        setCsNetworkImportStatuses((prev) => {
            const existing = prev[importStatusPersonId]
            const isSame =
                existing &&
                existing.status === importStatus.status &&
                existing.hasStatusRow === importStatus.hasStatusRow &&
                existing.latestLogStatus === importStatus.latestLogStatus &&
                existing.errMsg === importStatus.errMsg &&
                existing.statusId === importStatus.statusId
            if (isSame) {
                return prev
            }
            return {
                ...prev,
                [importStatusPersonId]: importStatus,
            }
        })
    }, [importStatus, importStatusPersonId])

    const handleEnd = useCallback(async () => {
        setIsStopping(true)
        setError(null)

        if (callMode === 'mock') {
            const endedConversationId =
                conversationIdRef.current?.trim() || null

            setMockStatus('disconnecting')

            if (endedConversationId) {
                await markConvoCallEndedInBackend(endedConversationId)
            }

            await new Promise((resolve) => setTimeout(resolve, 250))
            setMockStatus('disconnected')
            pendingRealConvoStartRef.current = null
            setIsStopping(false)
            setCallMode(null)
            return
        }

        try {
            await endSession()
        } catch (stopError) {
            console.error('Failed to end ElevenLabs conversation', stopError)
            const message =
                stopError instanceof Error
                    ? stopError.message
                    : 'Unable to end the call. Please try again.'
            setError(message)
        } finally {
            pendingRealConvoStartRef.current = null
            setIsStopping(false)
            setCallMode(null)
        }
    }, [callMode, endSession, markConvoCallEndedInBackend])

    const agentState = useMemo(() => {
        if (effectiveStatus !== 'connected') return null
        if (effectiveConversationMode === 'speaking') return 'talking'
        if (effectiveConversationMode === 'listening') return 'listening'
        return null
    }, [effectiveConversationMode, effectiveStatus])

    const canStart =
        effectiveStatus === 'disconnected' &&
        Boolean(agentId.trim()) &&
        !isStarting &&
        !isSearching &&
        !isFindingRandom &&
        !isPreparingPerson
    const canStartMock =
        effectiveStatus === 'disconnected' &&
        !isStarting &&
        !isSearching &&
        !isFindingRandom &&
        !isPreparingPerson
    const showHangUp =
        effectiveStatus === 'connected' || effectiveStatus === 'disconnecting'
    const canHangUp = effectiveStatus === 'connected' && !isStopping

    if (loadingSession) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-gray-50"
                style={kitTestThemeVars}
            >
                <LoadingSpinner />
            </div>
        )
    }

    if (!sessionInfo) {
        return null
    }

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={kitTestThemeVars}
        >
            <header className="w-full py-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    <img
                        src="Contxt_Logo_Full_BLK_CROPPED.png"
                        alt="Contxt"
                        className="h-10 w-auto"
                    />
                </div>
            </header>

            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={containerClass}>
                        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
                            <div className="min-w-0">
                                <div className="flex w-full flex-col space-y-6">
                                    <KitTestIntroCard />
                                    <ProfileSourceCard
                                        sourceChoice={sourceChoice}
                                        onSourceChange={handleSourceChange}
                                        isActiveCall={isActiveCall}
                                        sessionPerson={sessionInfo.person}
                                        linkedinUrl={linkedinUrl}
                                        onLinkedinUrlChange={setLinkedinUrl}
                                        onLinkedInLookup={handleLinkedInLookup}
                                        onFindRandom={handleFindRandom}
                                        isSearching={isSearching}
                                        isFindingRandom={isFindingRandom}
                                        lookupNote={lookupNote}
                                        csMatches={csMatches}
                                        selectedCsPersonId={selectedCsPersonId}
                                        onSelectMatch={handleSelectMatch}
                                        csNetworkImportStatuses={
                                            csNetworkImportStatuses
                                        }
                                        isLoadingNetworkStatuses={
                                            isLoadingNetworkStatuses
                                        }
                                        csNetworkConnectionCounts={
                                            csNetworkConnectionCounts
                                        }
                                        isLoadingNetworkConnectionCounts={
                                            isLoadingNetworkConnectionCounts
                                        }
                                    />
                                    <StartConversationCard
                                        error={error}
                                        agentId={agentId}
                                        onAgentIdChange={handleAgentIdChange}
                                        agentPresetId={agentPresetId}
                                        onAgentPresetChange={
                                            handleAgentPresetChange
                                        }
                                        agentPresetOptions={KIT_AGENT_PRESETS.map(
                                            (preset) => ({
                                                label: preset.label,
                                                agentId: preset.id,
                                            }),
                                        )}
                                        isAgentSelectionLocked={
                                            effectiveStatus !==
                                                'disconnected' ||
                                            isStarting ||
                                            isStopping
                                        }
                                        isCheckingImportStatus={
                                            isCheckingImportStatus
                                        }
                                        isPreparingPerson={isPreparingPerson}
                                        status={effectiveStatus}
                                        canStart={canStart}
                                        isStarting={isStarting}
                                        startMode={startMode}
                                        onStart={handleStart}
                                        canStartMock={canStartMock}
                                        onStartMock={handleStartMock}
                                        callMode={callMode}
                                        agentState={agentState}
                                        getOutputVolume={getOutputVolumeSafe}
                                        showHangUp={showHangUp}
                                        canHangUp={canHangUp}
                                        isStopping={isStopping}
                                        onHangUp={handleEnd}
                                    />
                                    <KitToolTesterCard
                                        activeConvoId={activeConvoId}
                                        isActiveCall={isActiveCall}
                                        apiKey={kitToolApiKey}
                                        onApiKeyChange={setKitToolApiKey}
                                        jobsOffered={jobsOffered}
                                    />
                                </div>
                            </div>
                            <div className="min-w-0 space-y-6">
                                <KitDynamicVariablesCard
                                    activeConvoId={activeConvoId}
                                    dynamicVariables={lastDynamicVariables}
                                />
                                <KitDebugConsole
                                    activeConvoId={activeConvoId}
                                    toolCalls={toolCalls}
                                    transcriptRawData={transcriptRawData}
                                    transcriptTurns={transcriptTurns}
                                    personMentions={personMentions}
                                    companyMentions={companyMentions}
                                    locationMentions={locationMentions}
                                    targetJobTitleMentions={
                                        targetJobTitleMentions
                                    }
                                    targetSalaryMentions={targetSalaryMentions}
                                    currentJobSearchParams={
                                        currentJobSearchParams
                                    }
                                    jobsOffered={jobsOffered}
                                    jobSearchExecutions={jobSearchExecutions}
                                    scoring={scoring}
                                    kitConvoId={null}
                                    onRetriggerComplete={() => {}}
                                    toolCallsError={toolCallsError}
                                    flashingToolCallIds={flashingToolCallIds}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    )
}
